import { logger } from '@porsche-code/kado-logger/client';
import { AwsRum, type AwsRumConfig, type PageAttributes } from 'aws-rum-web';

declare global {
  interface Window {
    Cypress?: unknown;
    __E2E__?: boolean;
  }
}

export type RumConfig = Pick<AwsRumConfig, 'identityPoolId'> & {
  appId: string;
  appVersion: string;
  region?: string;
};

export type RumAddSessionAttributes = {
  [key: string]: string | boolean | number;
};

export type RumRecordError = Error;

export type RumRecordPageView = string | PageAttributes;

export type RumEventType = string;
export type RumEventData = object;

class RumClass {
  private awsRum?: AwsRum;

  public configure({
    appId,
    appVersion,
    identityPoolId,
    region = 'eu-central-1',
  }: RumConfig): void {
    if (this.awsRum) {
      logger.warn('Rum#configure: RUM already configured');
      return;
    }

    const enableRumClient =
      process.env.NODE_ENV === 'production' &&
      !window.Cypress &&
      !window.__E2E__ &&
      !!appId;
    if (!enableRumClient) {
      logger.warn('Rum#configure: RUM disabled');
      return;
    }

    const config: AwsRumConfig = {
      disableAutoPageView: true,
      sessionSampleRate: 1,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: [
        'performance',
        'errors',
        ['http', { addXRayTraceIdHeader: true }],
      ],
      allowCookies: true,
      enableXRay: true,
      signing: true,
      identityPoolId,
      enableRumClient,
    };

    try {
      const rum = new AwsRum(appId, appVersion, region, config);
      this.awsRum = rum;
    } catch (error) {
      logger.error(error, 'Rum#configure: Failed to configure RUM');
    }
  }

  public addSessionAttributes(attributes: RumAddSessionAttributes): void {
    try {
      this.awsRum?.addSessionAttributes(attributes);
      logger.debug(
        attributes,
        'Rum#addSessionAttributes: Added session attributes',
      );
    } catch (error) {
      logger.error(
        error,
        'Rum#addSessionAttributes: Failed to add session attributes',
      );
    }
  }

  public recordError(error: RumRecordError): void {
    try {
      this.awsRum?.recordError(error);
      logger.debug(error, 'Rum#recordError: Recorded error');
    } catch (rumError) {
      logger.error(rumError, 'Rum#recordError: Failed to record error');
    }
  }

  public recordEvent(eventType: RumEventType, eventData: RumEventData): void {
    try {
      this.awsRum?.recordEvent(eventType, eventData);
      logger.debug({ eventType, eventData }, 'Rum#recordEvent: Recorded event');
    } catch (rumError) {
      logger.error(rumError, 'Rum#recordEvent: Failed to record event');
    }
  }

  public recordPageView(payload: RumRecordPageView): void {
    try {
      this.awsRum?.recordPageView(payload);
      logger.debug(payload, 'Rum#recordPageView: Recorded page view');
    } catch (error) {
      logger.error(error, 'Rum#recordPageView: Failed to record page view');
    }
  }
}

export const Rum = new RumClass();
